/**
 * PRICING CONFIGURATION
 * ─────────────────────
 * This is the single place to change plan prices.
 * Update the numbers below → redeploy → done.
 * No Stripe dashboard changes needed.
 *
 * Requires STRIPE_PRODUCT_ID in .env (one product created once in Stripe dashboard;
 * prices are created dynamically against this product).
 */

export interface BillingOption {
  price: number;        // price in USD dollars
  priceInCents: number; // price in cents (used for Stripe)
}

export interface PlanConfig {
  id: number;   // internal planId (matches tenants.plan_id)
  name: string; // display name
  monthly: BillingOption;
  yearly: BillingOption;
  features: string[];
}

export const PLANS: Record<string, PlanConfig> = {
  single: {
    id: 2,
    name: 'Single',
    monthly: { price: 25, priceInCents: 2500 },
    yearly: { price: 250, priceInCents: 25000 },
    features: [
      'Up to 2 users (including admin)',
      'Up to 25 people management',
      'Up to 50 SaaS applications',
      '120 license limit',
      'Basic reporting',
      'Email support',
      'License renewal tracking',
    ],
  },
  team: {
    id: 3,
    name: 'Team',
    monthly: { price: 39, priceInCents: 3900 },
    yearly: { price: 390, priceInCents: 39000 },
    features: [
      'Up to 4 users (including admin)',
      'Up to 100 people management',
      'Up to 100 SaaS applications',
      '250 license limit',
      'Advanced reporting',
      'Priority email support',
      'License renewal tracking',
      'Team management',
    ],
  },
  enterprise: {
    id: 4,
    name: 'Enterprise',
    monthly: { price: 199, priceInCents: 19900 },
    yearly: { price: 1990, priceInCents: 199000 },
    features: [
      'Up to 15 users (including admin)',
      'Unlimited people management',
      'Unlimited SaaS applications',
      'Unlimited licenses',
      'Advanced reporting',
      'Priority email support',
      'License renewal tracking',
      'Dedicated support',
      'Custom integrations',
      'Advanced security',
    ],
  },
};

export type BillingCycle = 'monthly' | 'yearly';

/** Resolve plan key from internal planId */
export function getPlanKeyById(planId: number): string {
  return Object.keys(PLANS).find(k => PLANS[k].id === planId) ?? 'single';
}

/** Resolve plan key + billing cycle from a price in cents (for webhook fallback) */
export function getPlanByAmount(amountInCents: number): { planKey: string; billingCycle: BillingCycle } | null {
  for (const key of Object.keys(PLANS)) {
    if (PLANS[key].monthly.priceInCents === amountInCents) return { planKey: key, billingCycle: 'monthly' };
    if (PLANS[key].yearly.priceInCents === amountInCents) return { planKey: key, billingCycle: 'yearly' };
  }
  return null;
}
